import React, { useEffect, useMemo, useState } from "react";
import { personalDetailsValidationSchema } from "../../utils/validationSchema";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "../../components/ui";
import FormInput from "../../components/ui/FormInput";
import {
  personalDetailsFields,
  handleFileChange,
  memberPersonalDetailsFields,
} from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/shared/AuthContext";
import ErrorMessage from "../../components/shared/ErrorMessage";
import { debounce } from "../../utils/debounce";

const PersonalDetails = ({
  personalDetailsData,
  setPersonalDetailsData,
  verificationDetailsData,
  setVerificationDetailsData,
  customerId,
  handleNext,
  type,
  // Disbursed loan: fields are already read-only via type="View"; accepted
  // here so the prop is declared rather than leaking to the DOM.
  locked = false,
  mode,
}) => {
  const navigate = useNavigate();
  const { data, roleName } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(personalDetailsValidationSchema),
    defaultValues: personalDetailsData,
    // Drives the profile/signature image rule in the schema. Both are demanded
    // only when a NEW customer is being created on the Customers screen — the
    // one place they can be uploaded. Editing an existing customer, and the
    // read-only loan wizard, must not be blocked by an image that predates the
    // rule (10 of 12 live customers have neither).
    context: { isAdd: type === "Add" && mode === "customer" },
  });


  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldsData, setFieldsData] = useState([]);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationErrorsList, setValidationErrorsList] = useState([]);
  const [aadharNo, setAadharNo] = useState("");
  const [message, setMessage] = useState("");
  const formatAadhar = (rawAadhar) => {
    if (!rawAadhar) return "";
    const clean = String(rawAadhar).replace(/\D/g, "");
    const digits = clean.substring(0, 12);
    let formatted = "";
    if (digits.length > 0) {
      formatted += digits.substring(0, 4);
    }
    if (digits.length > 4) {
      formatted += "-" + digits.substring(4, 8);
    }
    if (digits.length > 8) {
      formatted += "-" + digits.substring(8, 12);
    }
    return formatted;
  };


  useEffect(() => {
    const fetchFieldsData = async () => {
      // For Super Admin, pass null (no branch filter) to show all field officers
      // For Branch Admin / Data Entry Operator, pass their branchId to filter
      const isSuperAdmin = roleName === 'superadmin';
      const branchIdForFilter = isSuperAdmin ? null : data?.branchId;

      let fieldsResult;
      if (mode === "customer") {
        fieldsResult = await personalDetailsFields(branchIdForFilter);
      } else if (mode === "businessLoan") {
        fieldsResult = await memberPersonalDetailsFields(branchIdForFilter);
      }
      setFieldsData(fieldsResult);
    };

    fetchFieldsData();
  }, [mode, roleName, data?.branchId]);

  useEffect(() => {
    if (personalDetailsData) {
      Object.keys(personalDetailsData).forEach((key) => {
        setValue(key, personalDetailsData[key]);

        // Check if the field corresponds to an image
        if (key.endsWith("Image") && personalDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${personalDetailsData[key]}`, // Construct the image URL
          }));
        }
      });
    }

    if (verificationDetailsData?.aadharNo) {
      const formatted = formatAadhar(verificationDetailsData.aadharNo);
      setAadharNo(formatted);
      setValue("aadharNo", verificationDetailsData.aadharNo, { shouldValidate: true });
    }
  }, [personalDetailsData, verificationDetailsData, setValue]);

  const dob = watch("dob");
  const sameAsPermanentAddress = watch("sameAsPermanentAddress");

  // Spouse's Name applies only to a married applicant. Compared to the exact
  // string the dropdown stores (utils.js) — renaming those option values would
  // silently break this check.
  const maritalStatus = watch("maritalStatus");
  const isMarried = String(maritalStatus || "").trim() === "Married";

  // A saved customer who is already not-married yet HAS a spouse name. 14 such
  // rows exist on jothilinga2, and the values are not spouse names at all —
  // "S/O MURUGAN", "D/O BALAMURUGAN" — a father's name entered in the wrong box.
  // Real data either way, so it must not be wiped just because someone opened
  // the record.
  //
  // Read from the LOADED record, never from watch(): derived from the live value
  // this would flip as soon as the operator changed the dropdown, and the clear
  // below would fire on exactly the rows it must not touch.
  const hadSpouseWhileNotMarried = Boolean(
    personalDetailsData?.id &&
    String(personalDetailsData?.maritalStatus || "").trim() !== "Married" &&
    personalDetailsData?.spouseName
  );

  // Clear the value when the field is hidden, so an Unmarried record cannot be
  // saved carrying a spouse name typed under Married.
  useEffect(() => {
    if (isMarried) return;
    if (hadSpouseWhileNotMarried) return;
    setValue("spouseName", "");
  }, [isMarried, hadSpouseWhileNotMarried, setValue]);

  const permDoorNo = watch("permanentDoorNo");
  const permStreet = watch("permanentStreetAddress");
  const permVillage = watch("permanentVillage");
  const permCity = watch("permanentCity");
  const permState = watch("permanentState");
  const permPostal = watch("permanentPostalCode");

  useEffect(() => {
    if (dob) {
      const age = calculateAge(dob);
      setValue("age", age, { shouldValidate: true }); // Set the calculated age and trigger validation
    }
  }, [dob, setValue]);

  const calculateAge = (dob) => {
    let birthDate;
    if (dob && typeof dob === "string") {
      const cleaned = dob.trim();
      if (cleaned.includes("-")) {
        const parts = cleaned.split("-");
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          birthDate = new Date(cleaned);
        } else {
          // DD-MM-YYYY
          const [day, month, year] = parts;
          birthDate = new Date(`${year}-${month}-${day}`);
        }
      } else if (cleaned.includes(".")) {
        // DD.MM.YYYY
        const [day, month, year] = cleaned.split(".");
        birthDate = new Date(`${year}-${month}-${day}`);
      } else {
        birthDate = new Date(cleaned);
      }
    } else {
      birthDate = new Date(dob);
    }

    if (!birthDate || isNaN(birthDate.getTime())) {
      return "";
    }

    const currentDate = new Date();
    const age = currentDate.getFullYear() - birthDate.getFullYear();
    const month = currentDate.getMonth() - birthDate.getMonth();
    if (
      month < 0 ||
      (month === 0 && currentDate.getDate() < birthDate.getDate())
    ) {
      return age - 1;
    }
    return age;
  };

  useEffect(() => {
    if (sameAsPermanentAddress) {
      // If sameAsPermanentAddress is true, copy the permanent address to current address fields
      setValue("currentDoorNumber", permDoorNo, { shouldValidate: true });
      setValue("currentStreetAddress", permStreet, { shouldValidate: true });
      setValue("currentVillage", permVillage, { shouldValidate: true });
      setValue("currentCity", permCity, { shouldValidate: true });
      setValue("currentState", permState, { shouldValidate: true });
      setValue("currentPostalCode", permPostal, { shouldValidate: true });
    }
  }, [
    sameAsPermanentAddress,
    permDoorNo,
    permStreet,
    permVillage,
    permCity,
    permState,
    permPostal,
    setValue
  ]);

  // Aadhaar uniqueness check.
  //
  // This used to download EVERY aadharNo in the system (13k+ rows) on page load
  // and test membership locally. That was slow and exposed the full list of a
  // sensitive identifier to the browser. It also had no notion of ownership, so
  // a customer's own Aadhaar was reported as "already registered".
  //
  // Now: look up the single number and compare its owner.
  //   own Aadhaar        -> valid (short-circuits, no request at all)
  //   nobody's Aadhaar   -> valid
  //   another customer's -> error, blocks submission
  const ownAadhar = String(verificationDetailsData?.aadharNo ?? "");

  const checkAadharOwner = useMemo(
    () =>
      debounce(async (digits) => {
        // The customer's own Aadhaar is always valid — no request needed.
        if (digits && digits === ownAadhar) {
          setMessage("");
          clearErrors("aadharNo");
          return;
        }
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/getModelData/verification_details` +
              `?fields=customerId&filter=${encodeURIComponent(
                JSON.stringify({ aadharNo: digits })
              )}`
          );
          const owner = response.data?.data?.[0]?.customerId;
          if (owner != null && String(owner) !== String(customerId ?? "")) {
            setMessage("taken");
            setError("aadharNo", {
              type: "manual",
              message: "Already registered to another customer.",
            });
          } else {
            setMessage("available");
            clearErrors("aadharNo");
          }
        } catch (error) {
          // A failed lookup must not block the form. The value is still
          // validated server-side on submit.
          console.error("Error checking Aadhar:", error);
          setMessage("");
          clearErrors("aadharNo");
        }
      }, 400),
    [ownAadhar, customerId, setError, clearErrors]
  );

  // Cancel any in-flight debounce when the component unmounts.
  useEffect(() => () => checkAadharOwner.cancel(), [checkAadharOwner]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);


  const onSubmit = async (formData) => {
    console.log("clicked onSubmit in personal details");

    // Aadhaar Uniqueness Check
    if (message === "taken") {
      setErrorMsg("Aadhaar number is already registered in the system.");
      return;
    }

    console.log("Form Data:", JSON.stringify(formData));
    const formDataObj = new FormData();

    // Get branchId from auth data and ensure it's a single value
    const branchId = data?.branchId || null;
    // Handle case where branchId might be an array - take the first value
    const singleBranchId = Array.isArray(branchId) ? branchId[0] : branchId;
    console.log("Using branchId:", singleBranchId);

    // branchId is required on personal_details. Fail here with a clear message
    // rather than posting a request the server can only reject.
    if (!singleBranchId) {
      setErrorMsg(
        "Your account has no branch assigned, so a customer cannot be created. Please contact an administrator."
      );
      return;
    }

    Object.entries(formData).forEach(([key, value]) => {
      // personal_details has no column for either of these, so neither may go in
      // under its own name. branchId is re-appended below as a single value;
      // aadharNo is forwarded as `verificationAadharNo` after this loop.
      if (key === 'branchId' || key === 'aadharNo') {
        return;
      }

      if (filePreviews[key]?.file) {
        formDataObj.append(key, filePreviews[key].file);
      } else {
        // Append other non-file fields
        if (value !== null && value !== "") {
          formDataObj.append(key, value);
        }
      }
    });

    formDataObj.append("model", "personal_details");
    formDataObj.append("type", type);
    // Append branchId as a single value
    if (singleBranchId) {
      formDataObj.append("branchId", singleBranchId);
    }

    // Forward the Aadhaar so it reaches the table that CAN hold it.
    //
    // It was previously dropped entirely: personal_details has no column for it,
    // so the operator typed it here, the value went nowhere, and the Verification
    // tab — which reads verification_details — opened blank and made them type it
    // again. addDetailsWithImages.js upserts it onto verification_details under
    // this name.
    //
    // DIGITS ONLY. The visible field is dashed (1111-2222-3333) via formatAadhar,
    // but stored values are 12 plain digits — the duplicate check above queries on
    // digits, so storing the dashed form would stop it matching.
    //
    // Nothing is sent when the field is blank: Aadhaar is optional at this step
    // and an empty verification row serves no purpose.
    const aadharDigits = String(formData.aadharNo || "").replace(/\D/g, "");
    if (aadharDigits) {
      formDataObj.append("verificationAadharNo", aadharDigits);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetailsWithImages`,
        formDataObj,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true, // If cookies are needed
        }
      );

      if (response.status === 201) {
        setPersonalDetailsData(response.data.savedData);
        setShowValidationAlert(false);
        console.log("Personal Details added successfully: ", response.data);
        if (type === "Add") {
          handleNext();
        }
        if (type === "Edit") {
          if (mode === "customer") {
            navigate("/addEditCustomer", {
              state: {
                type: "View",
                stepNo: 0,
                customerId: personalDetailsData.id,
              },
            });
          }
          if (mode === "businessLoan") {
            handleNext();
          }
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error adding personal details");
      }
    } catch (error) {
      console.error("Error Adding Personal Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error adding personal details";
      if (mode === "businessLoan" && error.response?.status === 403) {
        handleNext();
        return;
      }
      setErrorMsg(msg);
    }
  };
  const onError = (errors) => {
    const errorMessages = Object.keys(errors).map((key) => {
      return errors[key].message || `${key} is required`;
    });

    setValidationErrorsList(errorMessages);
    setShowValidationAlert(true);
  };

  const personalFields = fieldsData.filter(field =>
    !field.name.startsWith("permanent") &&
    !field.name.startsWith("current") &&
    field.name !== "sameAsPermanentAddress" &&
    field.name !== "citizenship" &&
    field.name !== "customerProfileImage" &&
    field.name !== "customerSignatureImage" &&
    // An unmarried applicant has no spouse to name. Hidden rather than disabled:
    // a greyed-out Spouse's Name beside "Unmarried" still reads as a question
    // the operator failed to answer.
    //
    // "Others" (widowed, divorced, separated) is treated as not-married here.
    (field.name !== "spouseName" || isMarried)
  );

  const addressFields = fieldsData.filter(field =>
    field.name.startsWith("permanent") ||
    field.name.startsWith("current") ||
    field.name === "sameAsPermanentAddress" ||
    field.name === "citizenship"
  );
  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden p-8 max-w-6xl mx-auto mt-6">
      {/* Closable Right-side Error Slide-in Alert */}
      {showValidationAlert && validationErrorsList.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 10000,
          width: '360px',
          backgroundColor: 'rgba(254, 242, 242, 0.98)',
          backdropFilter: 'blur(8px)',
          border: '1px solid #FCA5A5',
          borderRadius: '16px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          padding: '16px 20px 16px 16px',
          display: 'flex',
          gap: '12px',
          animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          fontFamily: "'Inter', sans-serif"
        }}>
          {/* Warning Icon */}
          <div style={{
            backgroundColor: '#EF4444',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            flexShrink: 0,
            marginTop: '2px'
          }}>!</div>

          {/* Error Message */}
          <div style={{ flex: 1 }}>
            <h4 style={{
              margin: '0 0 4px 0',
              color: '#991B1B',
              fontSize: '15px',
              fontWeight: '600'
            }}>
              Required Fields Missing
            </h4>
            <p style={{
              margin: 0,
              color: '#7F1D1D',
              fontSize: '13px',
              lineHeight: '1.4',
              fontWeight: '500'
            }}>
              Please fill in all mandatory fields marked with a red asterisk (*).
            </p>
          </div>

          {/* Close X Button */}
          <button
            type="button"
            onClick={() => setShowValidationAlert(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#991B1B',
              fontSize: '18px',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '0 4px',
              height: 'fit-content',
              opacity: 0.7,
              transition: 'opacity 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.target.style.opacity = 1}
            onMouseLeave={(e) => e.target.style.opacity = 0.7}
          >
            &times;
          </button>

          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(120%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}

      {/* ── Section: Personal Details ── */}
      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary ">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
            <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink-900">Personal Details</h2>
          <p className="text-xs text-ink-500 mt-0.5">Enter basic personal information about the customer</p>
        </div>
        {type === "View" && mode === "customer" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              if (mode === "customer") {
                navigate("/addEditCustomer", {
                  state: {
                    type: "Edit",
                    stepNo: 0,
                    customerId: personalDetailsData.id,
                  },
                });
              }
              if (mode === "businessLoan") {
                navigate("/addEditBusinessLoan", {
                  state: {
                    type: "Edit",
                    stepNo: 0,
                    customerId: personalDetailsData.id,
                  },
                });
              }
            }}
          >
            Edit Details
          </Button>
        )}
      </div>

      <ErrorMessage message={errorMsg} />

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {/* Personal Fields Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8">
          {/* Aadhaar Checker as first column item */}
          <div className="col-span-1">
            <Controller
              name="aadharNo"
              control={control}
              render={({ field }) => (
                <FormInput
                  label="Aadhaar Number"
                  type="text"
                  name="aadharNo"
                  placeholder="Enter Aadhaar Number"
                  readOnly={type === "View"}
                  required={true}
                  errors={errors}
                  value={aadharNo}
                  onChange={(e) => {
                    const val = e.target.value;
                    const clean = val.replace(/\D/g, "");
                    const digits = clean.substring(0, 12);

                    // Format as XXXX-XXXX-XXXX
                    let formatted = "";
                    if (digits.length > 0) {
                      formatted += digits.substring(0, 4);
                    }
                    if (digits.length > 4) {
                      formatted += "-" + digits.substring(4, 8);
                    }
                    if (digits.length > 8) {
                      formatted += "-" + digits.substring(8, 12);
                    }
                    setAadharNo(formatted);

                    // Update React Hook Form value
                    field.onChange(digits);

                    // Trigger uniqueness check
                    if (digits.length === 12) {
                      if (digits === ownAadhar) {
                        // The customer's own Aadhaar — valid, no lookup needed.
                        checkAadharOwner.cancel();
                        setMessage("");
                        clearErrors("aadharNo");
                      } else {
                        checkAadharOwner(digits);
                      }
                    } else {
                      checkAadharOwner.cancel();
                      setMessage("");
                      if (digits.length > 0) {
                        setError("aadharNo", { type: "manual", message: "Aadhar number must be 12 digits" });
                      } else {
                        setError("aadharNo", { type: "manual", message: "Aadhar is required" });
                      }
                    }
                  }}
                />
              )}
            />
            {message === "available" && (
              <p className="text-xs text-success-500 font-medium mt-1">
                Aadhaar is available.
              </p>
            )}
          </div>

          {personalFields
            .filter((field) => field.type !== "file")
            .map((field, index) => (
              <div key={index} className="col-span-1">
                <FormInput
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  register={register}
                  control={control}
                  watch={watch}
                  errors={errors}
                  options={field.options || []}
                  readOnly={
                    field.name === "age" ||
                    field.name === "citizenship" ||
                    (field.name.startsWith("current") &&
                      sameAsPermanentAddress) ||
                    type === "View" ||
                    field.readOnly
                  }
                  handleFileChange={(e) =>
                    handleFileChange(e, field.name, setFilePreviews)
                  }
                  existingFileUrl={existingFiles[field.name] || ""}
                  filePreview={filePreviews[field.name]?.previewUrl || ""}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              </div>
            ))}
        </div>


        {/* ── Section: Address Details ── */}
        <div className="pt-2 mb-6">
          <div className="flex items-start gap-3.5 mb-5">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
                <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.07-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-2.003 3.5-4.697 3.5-8.327a8 8 0 0 0-16 0c0 3.63 1.556 6.324 3.5 8.327a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-ink-900">Address Details</h2>
              <p className="text-xs text-ink-500 mt-0.5">Enter permanent address information</p>
            </div>
          </div>

          {/* Address Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5">
            {addressFields.map((field, index) => (
              <div
                key={index}
                className={
                  field.name === "sameAsPermanentAddress"
                    ? "md:col-span-3 col-span-1"
                    : "col-span-1"
                }
              >
                <FormInput
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  register={register}
                  control={control}
                  watch={watch}
                  errors={errors}
                  options={field.options || []}
                  readOnly={
                    field.name === "age" ||
                    field.name === "citizenship" ||
                    (field.name.startsWith("current") &&
                      sameAsPermanentAddress) ||
                    type === "View" ||
                    field.readOnly
                  }
                  handleFileChange={(e) =>
                    handleFileChange(e, field.name, setFilePreviews)
                  }
                  existingFileUrl={existingFiles[field.name] || ""}
                  filePreview={filePreviews[field.name]?.previewUrl || ""}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Image Upload Fields side-by-side at the bottom */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8 pt-4 border-t border-border-light">
          {fieldsData
            .filter((field) => field.type === "file")
            .map((field, index) => (
              <div key={index} className="col-span-1">
                <FormInput
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  register={register}
                  control={control}
                  watch={watch}
                  errors={errors}
                  options={field.options || []}
                  readOnly={type === "View" || field.readOnly}
                  handleFileChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setValue(field.name, file, { shouldValidate: true });
                    }
                    handleFileChange(e, field.name, setFilePreviews);
                  }}
                  existingFileUrl={existingFiles[field.name] || ""}
                  filePreview={filePreviews[field.name]?.previewUrl || ""}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              </div>
            ))}
        </div>

        {/* Action Buttons */}
        {!(type === "View") && (
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (type === "Edit") {
                  if (mode === "customer") {
                    navigate("/addEditCustomer", {
                      state: {
                        type: "View",
                        stepNo: 0,
                        customerId: personalDetailsData.id,
                      },
                    });
                  }
                  if (mode === "businessLoan") {
                    navigate("/addEditBusinessLoan", {
                      state: {
                        type: "View",
                        stepNo: 0,
                        customerId: personalDetailsData.id,
                      },
                    });
                  }
                } else {
                  navigate("/customers");
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {type === "Edit" ? "Update" : "Save & Next \u2192"}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PersonalDetails;